import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { getBidDetailsQuery, useUpdateBidMutation } from '@/app/hooks/queries/useBids';
import { Field, FieldControl, FieldError, FieldLabel } from '@/app/ui/components/form/field';
import { Textarea } from '@/app/ui/components/textarea';
import { Button } from '@/app/ui/components/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/ui/components/alert-dialog';
import { toastManager } from '@/app/ui/components/toast';
import { useTranslations } from 'use-intl';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '@/app/appContext';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { deleteBid } from '@/app/services/bidsService';
import { queryKeys } from '@/app/utils/constants';
import { useGetCompaniesQuery } from '@/app/hooks/queries/useCompanies';
import { Company } from '@/app/types/models';
import { Form } from '@/app/ui/components/form';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { CompanyCombobox } from '@/app/components/features/companies/CompanyCombobox';
import { Card } from '@/app/ui/components/card';
import { H3 } from '@/app/ui/components/heading';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/settings/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bidDetails');
  const tGlobal = useTranslations();
  const { bidId, tenderPackageId, projectId } = Route.useParams();
  const { userMeta } = useContext(AppContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { mutateAsync: updateBid, isPending: isUpdatingBid } = useUpdateBidMutation(
    bidId,
    tenderPackageId,
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bidAdditionalName, setBidAdditionalName] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const organizationId = userMeta?.organizationId ?? '';

  const { data: companies = [] } = useGetCompaniesQuery({
    organizationId,
    searchText: '',
    enabled: !!organizationId,
  });

  // Initialize form values when bid data loads
  useEffect(() => {
    if (bid) {
      setBidAdditionalName(bid.bidAdditionalName ?? '');
      setAdditionalInfo(bid.additionalInfo ?? '');

      if (companies.length > 0 && bid.companyId) {
        const company = companies.find((c) => c.id === bid.companyId);
        setSelectedCompany(company ?? null);
      }
    }
  }, [bid, companies]);

  const deleteMutation = useMutation({
    mutationFn: async () => deleteBid(bidId),
    onSuccess: async () => {
      toastManager.add({
        type: 'success',
        title: t('notifications.deleted', { companyName: bid?.companyName ?? '' }),
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.BIDS, tenderPackageId],
      });
      navigate({
        to: '/project/$projectId/tender-packages/$tenderPackageId/bids',
        params: { projectId, tenderPackageId },
      });
    },
    onError: () => {
      toastManager.add({
        type: 'error',
        title: tGlobal('general.notifications.error'),
      });
    },
  });

  const handleSubmit = async () => {
    if (!selectedCompany) {
      toastManager.add({
        type: 'error',
        title: tGlobal('general.validation.companyRequired'),
      });
      return;
    }
    await updateBid({
      companyId: selectedCompany.id,
      additionalInfo,
      bidAdditionalName: bidAdditionalName ?? '',
    });
    toastManager.add({
      type: 'success',
      title: tGlobal('general.notifications.updatedSuccessfully'),
    });
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync();
    setDeleteDialogOpen(false);
  };

  if (!bid || !evaluation) return null;

  return (
    <Form onFormSubmit={handleSubmit} className="max-w-xl w-full flex flex-col gap-4">
      <Card className="space-y-4">
        <H3 className="-mt-1">{t('info')}</H3>
        <Field>
          <FieldLabel>{t('formLabels.bidAdditionalName')}</FieldLabel>
          <FieldControl
            value={bidAdditionalName}
            onValueChange={setBidAdditionalName}
            placeholder={t('formLabels.bidAdditionalName')}
            maxLength={60}
          />
          <FieldError />
        </Field>
        <Field>
          <FieldLabel>{t('formLabels.supplier')}</FieldLabel>
          <CompanyCombobox
            companies={companies}
            value={selectedCompany}
            onValueChange={setSelectedCompany}
            disabled={isUpdatingBid}
            ariaLabel={t('formLabels.supplier')}
          />
          <FieldError />
        </Field>
        <Field>
          <FieldLabel>{t('formLabels.additionalInfo')}</FieldLabel>
          <FieldControl
            required
            value={additionalInfo}
            onValueChange={setAdditionalInfo}
            placeholder={t('formLabels.additionalInfo')}
            render={<Textarea className="min-h-28" />}
          />
          <FieldError />
        </Field>
      </Card>
      <Button type="submit" pending={isUpdatingBid} disabled={isUpdatingBid} className="ml-auto">
        {t('buttons.save')}
      </Button>
      <Card>
        <H3 className="-mt-1">{t('danger')}</H3>
        <p className="text-sm text-muted mt-2 mb-4">{t('dangerText')}</p>
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogTrigger render={<Button variant="destructive">{t('danger')}</Button>} />
          <AlertDialogPopup>
            <AlertDialogTitle>{t('modals.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('modals.delete.subtitle')}</AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel />
              <Button
                variant="destructive"
                onClick={handleDelete}
                pending={deleteMutation.isPending}
                disabled={deleteMutation.isPending}
              >
                {t('buttons.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      </Card>
    </Form>
  );
}
